import { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Progress } from '@/components/ui/progress';
import { ToggleGroup, ToggleGroupItem } from '@/components/ui/toggle-group';
import MetricsCard from '@/components/Dashboard/MetricsCard';
import { usePlanoAcaoResponsavel, PlanoAcaoComDetalhes } from '@/hooks/usePlanoAcaoResponsavel';
import { PlanoAcaoModalEnhanced } from './PlanoAcao/PlanoAcaoModalEnhanced';
import { PlanoAcaoCard } from './PlanoAcao/components/PlanoAcaoCard';
import { PlanosKanban } from './PlanoAcao/components/PlanosKanban';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { 
  ClipboardList, 
  Clock, 
  CheckCircle, 
  AlertTriangle,
  TrendingUp,
  ChevronLeft,
  ChevronRight,
  PlayCircle,
  LayoutList,
  LayoutGrid,
  Target,
} from 'lucide-react';

type ViewMode = 'lista' | 'kanban';
type FiltroPrioridadeGUT = 'todos' | 'critica' | 'alta' | 'media' | 'baixa';

const PlanoAcaoDashboard = () => {
  const { planosAcao, metricas, isLoading, refetch, calcularProgressoPlano } = usePlanoAcaoResponsavel();
  const [selectedPlano, setSelectedPlano] = useState<PlanoAcaoComDetalhes | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [filtroStatus, setFiltroStatus] = useState<string>('todos');
  const [filtroGUT, setFiltroGUT] = useState<FiltroPrioridadeGUT>('todos');
  const [viewMode, setViewMode] = useState<ViewMode>('lista');
  const itemsPerPage = 5;

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-lg">Carregando planos de ação...</div>
      </div>
    );
  }

  // Filtrar planos baseado no status e prioridade GUT
  const planosFiltrados = planosAcao
    ?.filter(p => filtroStatus === 'todos' || p.status === filtroStatus)
    ?.filter(p => {
      if (filtroGUT === 'todos') return true;
      const gutPrioridade = p.avaliacao_gut?.[0]?.prioridade;
      return gutPrioridade === filtroGUT;
    });

  // Paginação (apenas para modo lista)
  const totalPages = Math.ceil((planosFiltrados?.length || 0) / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const planosPaginados = planosFiltrados?.slice(startIndex, startIndex + itemsPerPage);

  const getPrioridadeGUTColor = (prioridade: FiltroPrioridadeGUT) => {
    switch (prioridade) {
      case 'critica': return 'bg-red-100 text-red-800 hover:bg-red-200 dark:bg-red-900 dark:text-red-200';
      case 'alta': return 'bg-orange-100 text-orange-800 hover:bg-orange-200 dark:bg-orange-900 dark:text-orange-200';
      case 'media': return 'bg-yellow-100 text-yellow-800 hover:bg-yellow-200 dark:bg-yellow-900 dark:text-yellow-200';
      case 'baixa': return 'bg-green-100 text-green-800 hover:bg-green-200 dark:bg-green-900 dark:text-green-200';
      default: return '';
    }
  };

  const handlePlanoClick = (plano: PlanoAcaoComDetalhes) => {
    setSelectedPlano(plano);
    setIsModalOpen(true);
  };

  return (
    <div className="space-y-6">
      {/* Header com título e toggle de visualização */}
      <section className="flex flex-col md:flex-row md:items-end md:justify-between animate-fade-in gap-4">
        <div className="flex items-center gap-3">
          <span className="rounded-full bg-gradient-to-br from-orange-500 to-red-600 p-2 shadow-lg">
            <ClipboardList className="h-6 w-6 text-white" />
          </span>
          <h1 className="text-2xl md:text-3xl font-extrabold tracking-tight gradient-heading">
            Meus Planos de Ação
          </h1>
        </div>
        
        <div className="flex items-center gap-4">
          {/* Toggle Lista/Kanban */}
          <ToggleGroup 
            type="single" 
            value={viewMode} 
            onValueChange={(value) => value && setViewMode(value as ViewMode)}
            className="border rounded-lg p-1"
          >
            <ToggleGroupItem value="lista" aria-label="Visualização em lista" className="gap-2">
              <LayoutList className="h-4 w-4" />
              <span className="hidden sm:inline">Lista</span>
            </ToggleGroupItem>
            <ToggleGroupItem value="kanban" aria-label="Visualização Kanban" className="gap-2">
              <LayoutGrid className="h-4 w-4" />
              <span className="hidden sm:inline">Kanban</span>
            </ToggleGroupItem>
          </ToggleGroup>
          
          <div className="text-sm text-muted-foreground font-medium">
            Última atualização: {format(new Date(), 'dd/MM/yyyy HH:mm', { locale: ptBR })}
          </div>
        </div>
      </section>

      {/* Cards de Métricas */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 animate-fade-in">
        <MetricsCard
          title="Total de Planos"
          value={metricas.total}
          icon={ClipboardList}
          trend={{ value: "Total", direction: "neutral" }}
          onClick={() => setFiltroStatus('todos')}
        />
        <MetricsCard
          title="Em Andamento"
          value={metricas.emAndamento}
          icon={PlayCircle}
          trend={{ value: "Ativo", direction: "up" }}
          className="border-l-4 border-l-yellow-500"
          onClick={() => setFiltroStatus('em_andamento')}
        />
        <MetricsCard
          title="Atrasados"
          value={metricas.atrasados}
          icon={AlertTriangle}
          trend={{ value: "Crítico", direction: "down" }}
          className="border-l-4 border-l-red-500"
        />
        <MetricsCard
          title="Concluídos"
          value={metricas.concluidos}
          icon={CheckCircle}
          trend={{ value: "Finalizados", direction: "up" }}
          className="border-l-4 border-l-green-500"
          onClick={() => setFiltroStatus('concluido')}
        />
      </div>

      {/* Filtros de Status */}
      <div className="space-y-3">
        <div className="flex gap-2 flex-wrap">
          {['todos', 'aberto', 'em_andamento', 'concluido', 'cancelado'].map(status => (
            <Button
              key={status}
              variant={filtroStatus === status ? 'default' : 'outline'}
              size="sm"
              onClick={() => {
                setFiltroStatus(status);
                setCurrentPage(1);
              }}
            >
              {status === 'todos' ? 'Todos' : status.replace('_', ' ').charAt(0).toUpperCase() + status.replace('_', ' ').slice(1)}
            </Button>
          ))}
        </div>
        
        {/* Filtros de Prioridade GUT */}
        <div className="flex items-center gap-2 flex-wrap">
          <span className="text-sm font-medium text-muted-foreground flex items-center gap-1">
            <Target className="h-4 w-4" />
            Prioridade GUT:
          </span>
          {(['todos', 'critica', 'alta', 'media', 'baixa'] as FiltroPrioridadeGUT[]).map(prioridade => (
            <Button
              key={prioridade}
              variant={filtroGUT === prioridade ? 'default' : 'outline'}
              size="sm"
              onClick={() => {
                setFiltroGUT(prioridade);
                setCurrentPage(1);
              }}
              className={filtroGUT === prioridade ? '' : getPrioridadeGUTColor(prioridade)}
            >
              {prioridade === 'todos' ? 'Todas' : prioridade.charAt(0).toUpperCase() + prioridade.slice(1)}
              {prioridade !== 'todos' && metricas.porPrioridadeGUT[prioridade] > 0 && (
                <Badge variant="secondary" className="ml-1 h-5 px-1.5">
                  {metricas.porPrioridadeGUT[prioridade]}
                </Badge>
              )}
            </Button>
          ))}
        </div>
      </div>

      {/* Conteúdo Principal - Lista ou Kanban */}
      {viewMode === 'kanban' ? (
        <PlanosKanban
          planos={planosFiltrados || []}
          onPlanoClick={handlePlanoClick}
          calcularProgressoPlano={calcularProgressoPlano}
          onRefetch={refetch}
        />
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 animate-fade-in">
          {/* Lista de Planos */}
          <Card className="rounded-xl shadow-md hover:shadow-xl border">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-orange-600 text-lg md:text-xl font-bold">
                <ClipboardList className="h-6 w-6" />
                Planos de Ação Recentes
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {!planosPaginados || planosPaginados.length === 0 ? (
                  <div className="text-center py-8 text-muted-foreground">
                    <ClipboardList className="h-12 w-12 mx-auto mb-3 text-muted" />
                    <p>Nenhum plano de ação encontrado</p>
                  </div>
                ) : (
                  <>
                    {planosPaginados.map((plano) => (
                      <PlanoAcaoCard
                        key={plano.id}
                        plano={plano}
                        onClick={() => handlePlanoClick(plano)}
                        calcularProgressoPlano={calcularProgressoPlano}
                      />
                    ))}
                    
                    {/* Paginação */}
                    {totalPages > 1 && (
                      <div className="flex items-center justify-between pt-4 border-t">
                        <div className="text-sm text-muted-foreground">
                          Página {currentPage} de {totalPages}
                        </div>
                        <div className="flex gap-2">
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                            disabled={currentPage === 1}
                          >
                            <ChevronLeft className="h-4 w-4" />
                          </Button>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                            disabled={currentPage === totalPages}
                          >
                            <ChevronRight className="h-4 w-4" />
                          </Button>
                        </div>
                      </div>
                    )}
                  </>
                )}
              </div>
            </CardContent>
          </Card>

          {/* Gráfico de Distribuição de Status */}
          <Card className="rounded-xl shadow-md hover:shadow-xl border">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-blue-600 text-lg md:text-xl font-bold">
                <TrendingUp className="h-6 w-6" />
                Distribuição por Status
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {/* Abertos */}
                <div>
                  <div className="flex justify-between items-center mb-2">
                    <span className="text-sm font-medium text-foreground">Abertos</span>
                    <span className="text-sm font-bold text-blue-600">
                      {metricas.abertos} ({Math.round((metricas.abertos / metricas.total) * 100) || 0}%)
                    </span>
                  </div>
                  <Progress 
                    value={(metricas.abertos / metricas.total) * 100} 
                    className="h-3"
                  />
                </div>
                
                {/* Em Andamento */}
                <div>
                  <div className="flex justify-between items-center mb-2">
                    <span className="text-sm font-medium text-foreground">Em Andamento</span>
                    <span className="text-sm font-bold text-yellow-600">
                      {metricas.emAndamento} ({Math.round((metricas.emAndamento / metricas.total) * 100) || 0}%)
                    </span>
                  </div>
                  <Progress 
                    value={(metricas.emAndamento / metricas.total) * 100} 
                    className="h-3"
                  />
                </div>
                
                {/* Concluídos */}
                <div>
                  <div className="flex justify-between items-center mb-2">
                    <span className="text-sm font-medium text-foreground">Concluídos</span>
                    <span className="text-sm font-bold text-green-600">
                      {metricas.concluidos} ({Math.round((metricas.concluidos / metricas.total) * 100) || 0}%)
                    </span>
                  </div>
                  <Progress 
                    value={(metricas.concluidos / metricas.total) * 100} 
                    className="h-3"
                  />
                </div>
                
                {/* Cancelados */}
                {metricas.cancelados > 0 && (
                  <div>
                    <div className="flex justify-between items-center mb-2">
                      <span className="text-sm font-medium text-foreground">Cancelados</span>
                      <span className="text-sm font-bold text-muted-foreground">
                        {metricas.cancelados} ({Math.round((metricas.cancelados / metricas.total) * 100) || 0}%)
                      </span>
                    </div>
                    <Progress 
                      value={(metricas.cancelados / metricas.total) * 100} 
                      className="h-3"
                    />
                  </div>
                )}
                
                {/* Resumo de Alertas */}
                <div className="pt-4 border-t space-y-2">
                  {metricas.atrasados > 0 && (
                    <div className="flex items-center gap-2 text-red-600 text-sm">
                      <AlertTriangle className="h-4 w-4" />
                      <span>{metricas.atrasados} plano(s) atrasado(s)</span>
                    </div>
                  )}
                  {metricas.proximoVencimento > 0 && (
                    <div className="flex items-center gap-2 text-orange-600 text-sm">
                      <Clock className="h-4 w-4" />
                      <span>{metricas.proximoVencimento} plano(s) vencem em 7 dias</span>
                    </div>
                  )}
                  {metricas.atrasados === 0 && metricas.proximoVencimento === 0 && (
                    <div className="flex items-center gap-2 text-green-600 text-sm">
                      <CheckCircle className="h-4 w-4" />
                      <span>Todos os planos dentro do prazo</span>
                    </div>
                  )}
                </div>
                
                {/* Métricas de Ações */}
                {metricas.totalAcoes > 0 && (
                  <div className="pt-4 border-t">
                    <div className="flex justify-between items-center mb-2">
                      <span className="text-sm font-medium text-foreground flex items-center gap-1">
                        <Target className="h-4 w-4" />
                        Ações Totais
                      </span>
                      <span className="text-sm font-bold text-primary">
                        {metricas.acoesConcluidas}/{metricas.totalAcoes}
                      </span>
                    </div>
                    <Progress 
                      value={(metricas.acoesConcluidas / metricas.totalAcoes) * 100} 
                      className="h-3"
                    />
                    <p className="text-xs text-muted-foreground mt-1">
                      {Math.round((metricas.acoesConcluidas / metricas.totalAcoes) * 100)}% das ações concluídas
                    </p>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Modal de Visualização/Edição */}
      {selectedPlano && (
        <PlanoAcaoModalEnhanced
          isOpen={isModalOpen}
          onClose={() => {
            setIsModalOpen(false);
            setSelectedPlano(null);
            refetch();
          }}
          planoExistente={selectedPlano as any}
          campoRespostaId={selectedPlano.formulario_campo_resposta_id || ''}
          respostaId={selectedPlano.resposta_id}
          campoId={selectedPlano.campo_id}
        />
      )}
    </div>
  );
};

export default PlanoAcaoDashboard;
